import { useQuery } from "@apollo/client";
import { Autocomplete, TextField } from "@mui/material";
import { gql } from "generated-graphql";
import { useEffect, useState } from "react";

const FACILITIES_QUERY = gql(`
  query EmailIntakeFacilityHint($search: String, $pageSize: Int) {
    facilities(search: $search, page: 0, pageSize: $pageSize) {
      items {
        id
        name
        city
        state
      }
    }
  }
`);

type FacilityOption = {
  id: string;
  name: string;
  city?: string | null;
  state?: string | null;
};

function optionLabel(facility: FacilityOption): string {
  const place = [facility.city, facility.state].filter(Boolean).join(", ");
  return place ? `${facility.name} — ${place}` : facility.name;
}

/**
 * Picks the facility a vendor mailbox corresponds to. A hint for humans reading the
 * grid — nothing routes on it, because manifests bind to facilities by EPA ID.
 *
 * Queries with an explicit `tenantIdOverride`: staff routes send an empty tenant
 * header, so without it this would search nothing.
 */
export function FacilityHintPicker({
  tenantId,
  value,
  onChange,
}: {
  tenantId: string;
  value: FacilityOption | null;
  onChange: (facility: FacilityOption | null) => void;
}) {
  // Seeded from the current selection, because `inputValue` is controlled: leaving it
  // empty renders a blank field on a sender that already has a facility. The dialog is
  // keyed and conditionally rendered, so this remounts per open and reseeds.
  const [inputValue, setInputValue] = useState(value ? optionLabel(value) : "");
  // What the reader actually typed, kept apart from what the field displays. Selecting
  // an option rewrites the display to that option's label, which is not a term to look
  // up — feeding it back would spend a request re-finding the row already chosen.
  const [typed, setTyped] = useState("");
  const [search, setSearch] = useState("");

  // Debounced so typing a facility name is one request, not one per character.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(typed), 400);
    return () => clearTimeout(timer);
  }, [typed]);

  const { data, loading } = useQuery(FACILITIES_QUERY, {
    variables: { search: search || null, pageSize: 20 },
    context: { tenantIdOverride: tenantId },
    fetchPolicy: "cache-and-network",
  });

  const options: FacilityOption[] = data?.facilities?.items ?? [];
  // Keep the current selection reachable even when it is outside the latest page of
  // results, or editing an entry would silently clear its hint.
  const withValue =
    value && !options.some((o) => o.id === value.id) ? [value, ...options] : options;

  return (
    <Autocomplete
      options={withValue}
      value={value}
      loading={loading}
      onChange={(_event, next) => onChange(next)}
      inputValue={inputValue}
      onInputChange={(_event, next, reason) => {
        // The field always shows what MUI says it should — the typed text, the label
        // of a chosen option, or empty after a clear. Only typing advances the search.
        setInputValue(next);
        if (reason === "input") setTyped(next);
      }}
      getOptionLabel={optionLabel}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(all) => all}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Facility"
          helperText="Which facility this sender's mail usually concerns. Manifests are still filed by the EPA ID on the manifest, so this does not decide the match."
        />
      )}
    />
  );
}
